import { useEffect, useMemo, useState } from "react";
import { Plus, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "../api";
import { inputCls, labelCls } from "../utils/datetime";

const EMPTY = { vendor_id: "", item_id: "", quantity: 1, purchase_price: 0 };

export default function Procurement() {
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const me = JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}");
  const canEdit = ["super_admin", "owner", "admin", "clinic_manager"].includes(me.level || "");

  const load = () => {
    api.get("/connect/list/procurement").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/connect/list/vendors").then((r) => setVendors((r.data || []).filter((v) => v.status !== "Inactive"))).catch(() => {});
    api.get("/connect/inventory").then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const total = useMemo(() => (parseInt(form.quantity, 10) || 0) * (parseInt(form.purchase_price, 10) || 0), [form]);
  const selectedItem = items.find((i) => i.id === form.item_id);

  const add = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/connect/procurement", {
        vendor_id: form.vendor_id, item_id: form.item_id,
        quantity: parseInt(form.quantity, 10), purchase_price: parseInt(form.purchase_price, 10) || 0,
      });
      toast.success(`${r.data.order.order} saved — stock updated to ${r.data.new_stock} ${selectedItem?.unit || "pcs"}`);
      setForm(EMPTY); setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save procurement"); }
  };

  return (
    <div data-testid="connect-procurement" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-pine-deep">Procurement</h2>
          <p className="text-sm text-slate-500">Vendor → item → quantity → save. Inventory stock updates automatically.</p>
        </div>
        {canEdit && (
          <button data-testid="proc-add-toggle" onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 rounded-full bg-teal-brand px-5 py-2.5 text-xs font-bold text-white hover:bg-pine transition-all">
            <Plus className="h-4 w-4" /> New Purchase
          </button>
        )}
      </div>

      {showForm && canEdit && (
        <form data-testid="proc-add-form" onSubmit={add} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className={labelCls}>Vendor *</label>
            <select required data-testid="proc-vendor" value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} className={`mt-1 ${inputCls}`}>
              <option value="">Select vendor</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Item *</label>
            <select required data-testid="proc-item" value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })} className={`mt-1 ${inputCls}`}>
              <option value="">Select item</option>
              {items.map((i) => <option key={i.id} value={i.id}>{i.item}{i.item_code ? ` (${i.item_code})` : ""} — stock {i.stock}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Quantity *</label>
            <input required type="number" min="1" inputMode="numeric" data-testid="proc-quantity" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className={`mt-1 ${inputCls}`} />
          </div>
          <div>
            <label className={labelCls}>Purchase Price (per {selectedItem?.unit || "unit"})</label>
            <input type="number" min="0" inputMode="numeric" data-testid="proc-price" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className={`mt-1 ${inputCls}`} />
          </div>
          <div className="flex flex-col justify-end">
            <p className="text-[11px] text-slate-400">Total: <span className="font-bold text-pine-deep">{inr(total)}</span></p>
            <button type="submit" data-testid="proc-submit" className="mt-1 h-11 rounded-full bg-pine text-ivory text-xs font-bold hover:bg-pine-deep transition-colors">Save & Update Stock</button>
          </div>
        </form>
      )}

      <div className="rounded-2xl bg-white border border-pine/8 shadow-soft overflow-hidden">
        <div className="overflow-x-auto" data-lenis-prevent>
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-pine/10 bg-cream/40">
                <th className="px-5 py-3.5 font-bold">Order</th>
                <th className="px-5 py-3.5 font-bold">Vendor</th>
                <th className="px-5 py-3.5 font-bold">Item</th>
                <th className="px-5 py-3.5 font-bold">Qty</th>
                <th className="px-5 py-3.5 font-bold">Amount</th>
                <th className="px-5 py-3.5 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`proc-row-${o.id.slice(0, 8)}`} className="border-b border-pine/5 last:border-0">
                  <td className="px-5 py-3.5 font-bold text-pine-deep">{o.order}</td>
                  <td className="px-5 py-3.5">{o.vendor}</td>
                  <td className="px-5 py-3.5">{o.item || o.items}{o.item_code ? ` · ${o.item_code}` : ""}</td>
                  <td className="px-5 py-3.5">{o.quantity || o.items}</td>
                  <td className="px-5 py-3.5 font-semibold">{inr(o.amount)}</td>
                  <td className="px-5 py-3.5"><span className="rounded-full bg-teal-soft px-2.5 py-1 text-[10px] font-bold text-teal-brand">{o.status}</span></td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">
                  <ShoppingCart className="h-8 w-8 mx-auto mb-2 text-slate-300" /> No purchases yet — record your first procurement above.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
